// frontend/src/components/AddSite.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './AddSite.css';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { CROP_CATEGORIES, SITE_SIZES, getCropIcon } from '../data/wizardOptions';
import { formatSiteSize } from '../utils/siteSizeUtils';
import { getSpeciesName, getSpeciesType, getSpeciesDescription } from '../utils/speciesUtils';
import SiteProcessingView from './SiteProcessingView';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STEPS = [
  { num: 1, key: 'location' },
  { num: 2, key: 'crop' },
  { num: 3, key: 'hazards' },
  { num: 4, key: 'size' },
  { num: 5, key: 'summary' }
];

export default function AddSite({ onComplete }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  const { farmId, refreshSummary, refreshDevices, setSelectedPlotId } = useFarm();


  const [createdSiteId, setCreatedSiteId] = useState(null);
  const [createdDeviceId, setCreatedDeviceId] = useState(null);

  const [crops, setCrops] = useState([]);
  const [loadingCrops, setLoadingCrops] = useState(true);

  const [formData, setFormData] = useState({
    name: 'Primary plot',
    coords: { lat: null, lng: null },
    crop: '',
    hazards: [],
    size: 1
  });

  const [loadingLoc, setLoadingLoc] = useState(false);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const [mapNode, setMapNode] = useState(null);

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        const res = await axios.get('/api/species/crops', { withCredentials: true });
        const fetchedCrops = res.data.crops || [];

        const formattedCrops = fetchedCrops.map((c) => ({
          ...c,
          icon: getCropIcon(c)
        }));

        setCrops(formattedCrops);

        if (formattedCrops.length > 0) {
          const initialCrop = formattedCrops[0];
          const recommendedHazards = (initialCrop.hazards || [])
            .filter((h) => h.recommended)
            .map((h) => h.id);

          setFormData((prev) => ({
            ...prev,
            crop: initialCrop.id,
            hazards: recommendedHazards
          }));
        }
      } catch (err) {
        console.error('Failed to load species options:', err);
        setError(t('errors.serverError') || 'Failed to load species catalog');
      } finally {
        setLoadingCrops(false);
      }
    };

    fetchCrops();
  }, [t]);

  useEffect(() => {
    if (step === 1 && (!formData?.coords?.lat || !formData?.coords?.lng)) {
      detectLocation();
    }
  }, [step]);

  useEffect(() => {
    if (!mapNode) return;

    const lat = Number(formData?.coords?.lat) || 40.7128;
    const lng = Number(formData?.coords?.lng) || -74.0060;

    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }

    if (mapNode._leaflet_id) {
      mapNode._leaflet_id = null;
    }

    try {
      const map = L.map(mapNode).setView([lat, lng], 12);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        subdomains: 'abcd',
        maxZoom: 19
      }).addTo(map);

      const marker = L.marker([lat, lng], { draggable: true }).addTo(map);

      marker.on('dragend', (e) => {
        const { lat: newLat, lng: newLng } = e.target.getLatLng();
        setFormData((prev) => ({
          ...prev,
          coords: {
            lat: parseFloat(newLat.toFixed(4)),
            lng: parseFloat(newLng.toFixed(4))
          }
        }));
      });

      mapInstance.current = map;
      markerRef.current = marker;

      setTimeout(() => {
        map.invalidateSize();
      }, 150);
    } catch (err) {
      console.error('[Leaflet Init Error]:', err);
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        markerRef.current = null;
      }
    };
  }, [mapNode]);

  const detectLocation = () => {
    setLoadingLoc(true);

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      setLoadingLoc(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(4));
        const lng = parseFloat(pos.coords.longitude.toFixed(4));

        setFormData((prev) => ({ ...prev, coords: { lat, lng } }));

        if (mapInstance.current && markerRef.current) {
          mapInstance.current.setView([lat, lng], 13);
          markerRef.current.setLatLng([lat, lng]);
        }
        setLoadingLoc(false);
      },
      (err) => {
        setLoadingLoc(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            alert('Location permission was denied. Please enable location access in browser settings.');
            break;
          case err.POSITION_UNAVAILABLE:
            alert('Location information is unavailable.');
            break;
          case err.TIMEOUT:
            alert('Location request timed out. Please try again.');
            break;
          default:
            alert('An unknown error occurred while retrieving location.');
        }
      },
      options
    );
  };

  const handleSearchAddress = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await res.json();

      if (data && data.length > 0) {
        const lat = parseFloat(parseFloat(data[0].lat).toFixed(4));
        const lng = parseFloat(parseFloat(data[0].lon).toFixed(4));

        setFormData((prev) => ({ ...prev, coords: { lat, lng } }));

        if (mapInstance.current && markerRef.current) {
          mapInstance.current.setView([lat, lng], 13);
          markerRef.current.setLatLng([lat, lng]);
        }
      } else {
        alert('Location not found. Try entering a city, town, or zip code.');
      }
    } catch (e) {
      console.error('Geocoding error:', e);
      alert('Failed to search location. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCrop = (cropId) => {
    const selectedCropObj = crops.find((c) => c.id === cropId);
    const cropHazards = selectedCropObj ? selectedCropObj.hazards || [] : [];
    let recommendedHazards = cropHazards
      .filter((h) => h.recommended)
      .map((h) => h.hazard_species_id ?? h.species_id ?? h.id)
      .filter(Boolean); // Guarantees no null, undefined, or empty strings
    recommendedHazards = [...new Set(recommendedHazards)];

    setFormData((prev) => ({
      ...prev,
      crop: cropId,
      hazards: recommendedHazards
    }));
  };

  const toggleHazard = (hazardId) => {
    if (!hazardId) return;

    setFormData((prev) => {
      // Clean existing state before toggling
      const currentHazards = (prev.hazards || []).filter(Boolean);
      const exists = currentHazards.includes(hazardId);

      const updated = exists
        ? currentHazards.filter((id) => id !== hazardId)
        : [...currentHazards, hazardId];

      return {
        ...prev,
        hazards: updated
      };
    });
  };

  const selectedCropObj = crops.find((c) => c.id === formData.crop);
  const availableHazards = selectedCropObj ? selectedCropObj.hazards || [] : [];

  const DISEASES = ['pest', 'fungus', 'virus', 'bacterium'];
  const filteredCrops =
    selectedDomain === 'all'
      ? crops.filter((c) => !DISEASES.includes(c.category))
      : crops.filter((c) => c.category === selectedDomain);



  const handleNext = async () => {
    if (step === 1 && !formData.name.trim()) {
      setError(t('wizard.errors.nameRequired') || 'Site name is required');
      return;
    }

    // 1. Step 1 Completion: Initialize Site & Trigger Extraction
    if (step === 1) {
      const effectiveFarmId = user?.propertyId || farmId;
      setSubmitting(true);
      setError('');

      try {
        let currentSiteId = createdSiteId;
        let currentDeviceId = createdDeviceId;

        // Initialize base site record if not created yet
        if (!currentSiteId) {
          const initPayload = {
            portfolioId: effectiveFarmId,
            name: formData.name,
            latitude: formData.coords.lat,
            longitude: formData.coords.lng
          };

          console.log('[AddSite.handleNext] Initializing site:', initPayload);
          const siteRes = await axios.post('/api/sites', initPayload, { withCredentials: true });
          
          currentSiteId = siteRes.data.site.id;
          currentDeviceId = siteRes.data.deviceId;

          setCreatedSiteId(currentSiteId);
          setCreatedDeviceId(currentDeviceId);
        }

        // Trigger proxy extraction with explicit siteId and propertyId
        const extractPayload = {
          siteId: currentSiteId,
          portfolioId: effectiveFarmId,
          lat: formData.coords.lat,
          lon: formData.coords.lng
        };

        console.log('[AddSite.handleNext] Trigger extract latlon:', extractPayload);
        await axios.post('/api/plots/extract-latlon', extractPayload, { withCredentials: true });

      } catch (err) {
        console.error('Error initializing site at Step 1:', err);
        setError(err.response?.data?.error || err.message || t('errors.serverError'));
        setSubmitting(false);
        return;
      } finally {
        setSubmitting(false);
      }
    }

    setError('');
    setStep((prev) => Math.min(prev + 1, 5));
  };



  const handleBack = () => {
    setError('');
    setStep((prev) => Math.max(prev - 1, 1));
  };



  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const effectiveFarmId = user?.propertyId || farmId;
      if (!createdSiteId) {
        throw new Error('Site has not been initialized.');
      }

      const finishPayload = {
        name: formData.name,
        latitude: formData.coords.lat,
        longitude: formData.coords.lng,
        areaHectares: formData.size,
        crop: formData.crop,
        hazards: formData.hazards
      };

      console.log(`[AddSite.handleSubmit] Finalizing site ${createdSiteId}:`, finishPayload);
      // PUT /api/sites/:siteId executes finishSite
      await axios.put(`/api/sites/${createdSiteId}`, finishPayload, { withCredentials: true });

      const analyticsPayload = {
        portfolioId: effectiveFarmId,
        siteId: createdSiteId,
        deviceId: createdDeviceId,
        name: formData.name,
        latitude: formData.coords.lat,
        longitude: formData.coords.lng,
        areaHectares: formData.size,
        crop: formData.crop,
        hazards: formData.hazards
      };

      setStep('processing');

      console.log(`[AddSite.handleSubmit] Triggering analytics for plot ${createdSiteId}:`, analyticsPayload);
      await axios.post(
        `/api/plots/${createdSiteId}/trigger-analytics`,
        analyticsPayload,
        { withCredentials: true }
      );

    } catch (err) {
      console.error('Error in site setup submission:', err);
      setError(err.response?.data?.error || err.message || t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };



  const handleProcessingComplete = async (summaryData) => {
    try {
      // Re-fetch fresh farm summary and device list into FarmContext
      if (refreshSummary) await refreshSummary();
      if (refreshDevices) await refreshDevices();

      // Set the active plot ID in context to the newly created site
      if (createdSiteId && setSelectedPlotId) {
        setSelectedPlotId(createdSiteId);
      }
    } catch (err) {
      console.error('Failed to refresh farm context after site setup:', err);
    }

    if (onComplete) {
      onComplete(summaryData);
    } else {
      navigate('/dashboard');
    }
  };

  if (loadingCrops) {
    return <div className="onboarding-card">{t('common.loading')}</div>;
  }

  return (
    <div className="onboarding-card">
      <div className="wizard-steps">
        {STEPS.map((s) => (
          <span key={s.num} className={`step-dot ${step === s.num ? 'active' : ''}`}>
            {s.num}. {t(`wizard.steps.${s.key}`)}
          </span>
        ))}
      </div>

      <div className="wizard-body">
        {error && <div className="wizard-error-alert">{error}</div>}

        {/* Step 1: Location & Site Name */}
        {step === 1 && (
          <>
            <h2>{t('wizard.titles.location')}</h2>
            <p className="wizard-subtext" style={{ textAlign: 'left' }}>{t('wizard.subtitles.location')}</p>

            <div className="form-group" style={{ textAlign: 'left', marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px' }}>
                {t('wizard.fields.siteName') || 'Site Name'}
              </label>
              <input
                type="text"
                className="input-text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Field 1, North Sector"
                required
              />
            </div>

            <div className="form-group" style={{ textAlign: 'left', marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px' }}>
                {t('wizard.fields.searchLocation')}
              </label>
              <p className="wizard-subtext" style={{ textAlign: 'left' }}>{t('wizard.fields.searchLocationDescription')}</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="input-text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchAddress();
                    }
                  }}
                  placeholder="ex: Montreal, QC or 3823 rue Laurier"
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleSearchAddress}
                  disabled={searching}
                  style={{ whiteSpace: 'nowrap', padding: '0 16px', width: '200px' }}
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="map-wrapper">
              {loadingLoc && <div className="map-loading-overlay">{t('common.loading')}</div>}
              <div ref={setMapNode} className="map-container" />
            </div>

            <div className="location-info">
              <span>
                Lat: {formData.coords.lat}, Lng: {formData.coords.lng}
              </span>
              <button className="btn-re-detect" onClick={detectLocation}>
                {t('wizard.actions.reDetect')}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Crop */}
        {step === 2 && (
          <>
            <h2>{t('wizard.titles.crop')}</h2>
            <p className="wizard-subtext">{t('wizard.subtitles.crop')}</p>

            <div className="domain-tabs">
              <button
                className={`domain-tab ${selectedDomain === 'all' ? 'selected' : ''}`}
                onClick={() => setSelectedDomain('all')}
              >
                {t('common.search') || 'All'}
              </button>
              {CROP_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  className={`domain-tab ${selectedDomain === cat.id ? 'selected' : ''}`}
                  onClick={() => setSelectedDomain(cat.id)}
                >
                  <span>{cat.icon}</span> {t(`wizard.categories.${cat.id}`) || cat.id}
                </button>
              ))}
            </div>

            <div className="crop-grid">
              {filteredCrops.map((crop) => (
                <div
                  key={crop.id}
                  className={`crop-tile ${formData.crop === crop.id ? 'selected' : ''}`}
                  onClick={() => handleSelectCrop(crop.id)}
                >
                  <span className="crop-icon">{crop.icon}</span>
                  <span className="crop-label">
                    {crop.varietal ? `${crop.varietal} ` : ''}{getSpeciesName(crop, t)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Step 3: Hazards (Displays commonName + description) */}
        {step === 3 && (
          <>
            <h2>{t('wizard.titles.hazards')}</h2>
            <p className="wizard-subtext">{t('wizard.subtitles.hazards')}</p>

            <div className="section-block">
              <span className="section-label">{t('dashboard.monitoring')}</span>
              {availableHazards.length === 0 ? (
                <p className="no-hazards-notice">No specific hazard monitoring models registered for this crop.</p>
              ) : (
                <div className="hazard-checkbox-list">
                  {availableHazards.map((hazard) => {
                    //console.log("[AddSite] Hazard:",hazard);
                    const hazardName = getSpeciesName(hazard, t);
                    const hazardType = getSpeciesType(hazard, t);
                    const hazardDesc = getSpeciesDescription(hazard, t);

                    return (
                      <label key={hazard.hazard_species_id} className="hazard-checkbox-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', marginBottom: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                        <input
                          type="checkbox"
                          checked={formData.hazards.includes(hazard.hazard_species_id)}
                          onChange={() => toggleHazard(hazard.hazard_species_id)}
                          style={{ marginTop: '4px' }}
                        />
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <strong style={{ fontSize: '14px', color: '#0f172a' }}>{hazardName} ({hazardType})</strong>
                            {hazard.recommended && (
                              <span className="default-badge">{t('wizard.badges.recommended') || 'Recommended'}</span>
                            )}
                          </div>
                          {hazardDesc && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
                              {hazardDesc}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Step 4: Scale */}
        {step === 4 && (
          <>
            <h2>{t('wizard.titles.size')}</h2>
            <p className="wizard-subtext">{t('wizard.subtitles.size')}</p>

            <div className="size-chips">
              {SITE_SIZES.map((sizeObj) => (
                <button
                  key={sizeObj.id}
                  className={`size-chip ${formData.size === sizeObj.minSize ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, size: sizeObj.minSize })}
                >
                  {t(`wizard.sizes.${sizeObj.id}`)}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 5: Summary */}
        {step === 5 && (() => {
          const uniqueHazardIds = [...new Set(formData.hazards.filter((id) => id != null && id !== ''))];

          return (
            <>
              <h2>{t('wizard.titles.summary')}</h2>
              <p className="wizard-subtext">{t('wizard.subtitles.summary')}</p>

              <div className="summary-cards">
                <div className="summary-card">
                  <span className="summary-card-icon">🏷️</span>
                  <div className="summary-card-content">
                    <strong>{t('wizard.fields.siteName') || 'Site Name'}</strong>
                    <p>{formData.name}</p>
                  </div>
                </div>

                <div className="summary-card">
                  <span className="summary-card-icon">📍</span>
                  <div className="summary-card-content">
                    <strong>{t('wizard.steps.location')}</strong>
                    <p>{formData.coords.lat}, {formData.coords.lng}</p>
                  </div>
                </div>

                <div className="summary-card">
                  <span className="summary-card-icon">🌱</span>
                  <div className="summary-card-content">
                    <strong>{t('wizard.steps.crop')}</strong>
                    <p>
                      {getSpeciesName(selectedCropObj || formData.crop, t)} ({formatSiteSize(formData.size, t)})
                    </p>
                  </div>
                </div>

                <div className="summary-card">
                  <span className="summary-card-icon">🛡️</span>
                  <div className="summary-card-content">
                    <strong>
                      {t('wizard.steps.hazards')} ({uniqueHazardIds.length})
                    </strong>
                    <div className="summary-hazards-list" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {uniqueHazardIds.map((hId) => {
                        const hObj = availableHazards.find(
                          (item) => (item.hazard_species_id || item.id || item.species_id) === hId
                        ) || hId;
                        const hName = getSpeciesName(hObj, t);
                        const hDesc = getSpeciesDescription(hObj, t);

                        return (
                          <div key={hId} className="summary-hazard-item" style={{ textAlign: 'left', padding: '6px 10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <span style={{ fontWeight: '600', fontSize: '13px', color: '#0f172a' }}>{hName}</span>
                            {hDesc && (
                              <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>{hDesc}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {step === 'processing' && (
          <SiteProcessingView
            siteId={createdSiteId}
            farmId={user?.propertyId}
            onComplete={handleProcessingComplete}
          />
        )}

        {step !== 'processing' && (
          <div className="wizard-actions">
            {/* Cannot modify location once set */}
            {step > 2 && (
              <button className="btn-secondary" onClick={handleBack} disabled={submitting}>
                {t('wizard.actions.back')}
              </button>
            )}

            {step < 5 ? (
              <button className="btn-primary" onClick={handleNext}>
                {t('wizard.actions.next')}
              </button>
            ) : (
              <button className="btn-primary-lg" onClick={handleSubmit} disabled={submitting}>
                {submitting ? t('common.loading') : t('wizard.actions.saveSite')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
